/**
 * Session security model — SEE-212 (MFA & Session Security Overhaul) / SEE-221.
 *
 * Single source of truth for two questions the app keeps asking:
 *   1. How strongly is the CURRENT session authenticated? (which login method)
 *   2. Is that strong factor still RECENT enough to trust for a sensitive action?
 *
 * It is consumed by the "step-up" gate that runs before a user links a bank
 * account (credit auto-pay, debit funding, and onboarding all funnel through
 * Plaid Link), and is built to be reused by every future login method.
 *
 * Design decisions (the "why"):
 *
 * - SSO / federated sessions DO step up (SEE-221 policy: the IdP authenticated
 *   the person, but linking a bank moves money, so we require a fresh possession
 *   factor). This is feasible because the federated Cognito identity is only an
 *   auth ALIAS for the same person: there is exactly one User row and one
 *   PaidolUser per company, and `custom:original_sub` resolves the SSO login
 *   back to that canonical user, which holds the phone. The OTP send path
 *   resolves the canonical sub (native -> sub, SSO -> custom:original_sub) to
 *   the DDB phone for everyone alike, so we CAN SMS an SSO user.
 *
 * - Passkeys (WebAuthn, SEE-213) are the ONLY method exempt from the gate:
 *   they are inherently multi-factor (possession + biometric/PIN, incl. Face ID
 *   / Touch ID / Windows Hello; NIST SP 800-63B AAL2). We read an eventual
 *   `custom:auth_method === 'passkey'` claim so the exemption lights up the
 *   moment SEE-213 injects it — until then it is a no-op and everyone steps up.
 *
 * - Password and SSO are single-factor for this gate and both trigger a step-up.
 *   After a successful SMS OTP we treat the session as MFA-fresh for a short
 *   window so the user is not re-prompted on every click in one sitting.
 *
 * - Recency / timezones: every timestamp here is epoch MILLISECONDS (UTC).
 *   Cognito's `auth_time` claim is epoch SECONDS, converted on read. Elapsed-
 *   time comparisons (`now - verifiedAt <= window`) are inherently timezone-
 *   independent — we never parse or format local time, so there is nothing to
 *   "account for" across timezones.
 */

export type AuthMethod = 'sso' | 'passkey' | 'password' | 'unknown';
export type MfaLevel = 'mfa' | 'single_factor';

/** The subset of Cognito ID-token claims we read to classify the session. */
export interface IdTokenClaims {
  /**
   * Present when the ACCOUNT is linked to an IdP. Deliberately NOT used to
   * classify the session: it reflects linkage, not how THIS session
   * authenticated (a password login of an SSO-linked user still carries it).
   */
  identities?: unknown;
  /**
   * The canonical User.id. Injected by PreTokenGeneration on EVERY login
   * since SEE-1456 (SSO: the resolved native id; password: equal to sub).
   * Its presence is NOT an SSO signal — classify via custom:auth_method.
   */
  'custom:original_sub'?: string;
  /** Future: injected by the passkey work (SEE-213). e.g. 'passkey' | 'password'. */
  'custom:auth_method'?: string;
  /** Cognito auth time, epoch SECONDS. */
  auth_time?: number;
}

/**
 * How long a password user's SMS step-up stays valid before we re-prompt.
 * Matches the 15-minute MFA window the epic uses for the passkey-creation
 * step-up (SEE-213, Story A1). Overridable per-environment via AppConfig.
 */
export const DEFAULT_STEP_UP_FRESHNESS_MS = 15 * 60 * 1000;

/** Classify the login method from the ID-token claims. */
export function deriveAuthMethod(claims: IdTokenClaims): AuthMethod {
  if (!claims) return 'unknown';

  // An explicit method claim wins once a login flow sets it (passkey work).
  const explicit = claims['custom:auth_method'];
  if (explicit === 'passkey') return 'passkey';
  if (explicit === 'password') return 'password';
  if (explicit === 'sso') return 'sso';

  // Legacy fallback for tokens minted before custom:auth_method existed
  // (SEE-213). SEE-1456 made custom:original_sub a universal claim (minted
  // for password logins too), so its PRESENCE no longer implies SSO — but
  // every token that carries it also carries auth_method, which returns
  // above, so this line is only ever reached by pre-SEE-213 tokens where
  // presence still meant SSO. Do NOT use the `identities` claim — it marks
  // account↔IdP linkage, not the session's auth method, so a password login
  // of an SSO-linked user would otherwise wrongly skip the gate (SEE-221: a
  // password admin with a stale SSO link got straight into Plaid).
  if (claims['custom:original_sub']) return 'sso';

  return 'password';
}

/**
 * Only passkey (WebAuthn: possession + biometric/PIN, incl. Face ID / Touch ID /
 * Windows Hello, NIST AAL2) is multi-factor by the login method itself. SSO and
 * password are both single-factor for this gate and must step up (SEE-221).
 */
export function methodSatisfiesMfa(method: AuthMethod): boolean {
  return method === 'passkey';
}

export interface SessionSecurity {
  authMethod: AuthMethod;
  /** Effective MFA level right now, factoring in a recent password step-up. */
  mfaLevel: MfaLevel;
  /** When MFA was last satisfied (login for sso/passkey, or last OTP), epoch ms. */
  mfaVerifiedAt?: number;
  /** True when `mfaLevel === 'mfa'`. */
  isMfaFresh: boolean;
}

export interface DeriveSessionSecurityArgs {
  claims: IdTokenClaims | undefined | null;
  /** Epoch ms of the last successful step-up OTP this session (from Redux). */
  stepUpVerifiedAt?: number;
  /** Override the freshness window (AppConfig). */
  freshnessMs?: number;
  /** Injected for testability; defaults to Date.now(). */
  now?: number;
}

export function deriveSessionSecurity({
  claims,
  stepUpVerifiedAt,
  freshnessMs = DEFAULT_STEP_UP_FRESHNESS_MS,
  now = Date.now(),
}: DeriveSessionSecurityArgs): SessionSecurity {
  const authMethod = deriveAuthMethod(claims || {});
  const methodMfa = methodSatisfiesMfa(authMethod);

  // Freshness applies to the step-up path (password AND SSO). Passkey is MFA by
  // virtue of the login method itself and is governed by session lifetime
  // (Epic B), not this short re-prompt window — expiring it here would wrongly
  // re-gate active passkey users.
  const stepUpFresh = typeof stepUpVerifiedAt === 'number' && now - stepUpVerifiedAt <= freshnessMs;

  const mfaLevel: MfaLevel = methodMfa || stepUpFresh ? 'mfa' : 'single_factor';

  const authTimeMs = typeof claims?.auth_time === 'number' ? claims.auth_time * 1000 : undefined;
  const mfaVerifiedAt = methodMfa ? authTimeMs ?? now : stepUpVerifiedAt;

  return { authMethod, mfaLevel, mfaVerifiedAt, isMfaFresh: mfaLevel === 'mfa' };
}

export interface BankLinkStepUpDecisionArgs {
  security: SessionSecurity;
}

/**
 * The bank-linking gate decision. Returns true when an SMS OTP step-up must run
 * before Plaid Link opens. Skipped ONLY when the session is already MFA (a
 * passkey login or a recent step-up OTP). There is deliberately no disable
 * switch: the step-up is an unconditional security control. A user with no
 * phone on file is routed to add one (the phone-change wizard authorizes via an
 * email code, so it needs no existing phone), never to a bypass. SSO is NOT
 * exempt — federated sessions step up like password (SEE-221).
 */
export function isBankLinkStepUpRequired({ security }: BankLinkStepUpDecisionArgs): boolean {
  return security.mfaLevel === 'single_factor';
}
